import React, {
  useCallback, useEffect, useState,
} from 'react';

import {
  isTerminal, isDeathOutcome, dodIsConfirmed, DISCHARGE_ERROR_CODE, DISCHARGE_PERMISSIONS,
  ADMISSION_PERMISSIONS, ADMISSION_ACCESS_ERROR_CODE, ADMISSION_ACCESS_ERROR_COPY, DOCTOR_REQUIRED_COPY, doctorDisplayName,
} from '../../../constants/admission';
import { usePermission } from '../../../context/PermissionContext';
import {
  getDischargeSummary, saveDischargeSummary, openDischargeSummaryPdf, NOT_PERMITTED_SUMMARY_COPY, fieldErrorCopy,
} from '../../../services/api/admissionDischargeApi';
import { patchAdmission } from '../../../services/api/admissionApi';
import useDischargeSnippetStore, { dischargeSnippetKey } from '../../../stores/useDischargeSnippetStore';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import NoAccessPanel, { NO_ACCESS_VARIANT } from '../../common/NoAccessPanel';
import { NonTreatingDoctorGate, isCareTeamLocked } from '../TreatingDoctorsCard';
import SummaryFields, {
  isSummaryValid, getHeaderDateRangeError, getInvestigationRowsError, toInvestigationRowsValue, LAB_LINES_KIND,
  isDischargeDateEmpty, DOD_EMPTY_WARNING_COPY,
} from './SummaryFields';

const LOCKED_NOTE = 'This admission is closed. The bill and summary can be reprinted, but not changed.';
const HIDDEN_VALUES_NOTE = "Some earlier notes are hidden by your organisation's current discharge summary.";
// Ratified (copy-ratified.md #41) — a doctor who is not on this admission's care team.
const NOT_TREATING_SUMMARY_COPY = ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR];

const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12px] font-semibold disabled:opacity-60';
// Ratified copy (copy-ratified-slice1.md §A #10), business stakeholder
// verdict 2026-09-17. Reuses the same transient-banner idiom
// `PrescriptionTemplateManager.jsx`'s `successMsg` already establishes
// (setTimeout auto-dismiss), rather than a new shared Toast component
// nothing else in this file needs (YAGNI).
function snippetSavedToastCopy(ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  return dn ? `Template saved to ${dn}'s templates.` : 'Template saved.';
}

/**
 * Every EDITABLE field's current value, keyed by canonical key (§2.2
 * `fields[]`). Non-editable keys (e.g. `ip_number`, `doctor_signature`) and
 * any key the server hid this org's copy of (simply absent from `fields[]`,
 * §2.7) are NEVER part of this map, so they can never ride along on the PUT
 * — the server would 422 `READ_ONLY_FIELD`/`FIELD_HIDDEN` if they did.
 * Seeding from `field.value` (the raw override/stored value, not `value ??
 * prefill`) means a header key nobody has touched in THIS session still
 * round-trips its prior override unchanged on save (full-object REPLACE,
 * §2.3 — an omitted key is cleared).
 *
 * `kind:"lab_lines"` guard (FE-C2, R.1 D7): a legacy STRING value is
 * impossible going forward (the server converts on read), but if the doctor
 * never touches this field this session, `values[key]` would otherwise
 * round-trip whatever `field.value` was UNCHANGED straight to the PUT — the
 * one path `SummaryFields`' own render-time guard can't reach. Guarded here
 * too, ONCE, with the SAME `toInvestigationRowsValue` SummaryFields uses, so
 * the wire never carries a string for this key regardless of whether the
 * doctor edits it this session.
 */
function buildEditableValues(fields = []) {
  const out = {};
  fields.forEach((field) => {
    if (!field || !field.editable) return;
    // FIX-7-FE (owner ruling, reverses lite-pr S4 AC5) — `discharged_at` is
    // no longer a print-time header override; it is edited via the
    // discharge PLAN setter (`patchAdmission`, `SummaryFields.jsx`'s
    // `DischargeDateField`) and must NEVER ride the summary's own `{values}`
    // PUT again. Excluded here (rather than merely never calling `onChange`
    // for it) so a stale `field.value` from an older save can never
    // resurface on the wire even in a session that never opens this field.
    if (field.key === 'discharged_at') return;
    const raw = field.value ?? null;
    out[field.key] = field.kind === LAB_LINES_KIND ? toInvestigationRowsValue(raw) : raw;
  });
  return out;
}

/**
 * Single derived gate for Save — every REASON the button is blocked lives
 * here, never re-derived per branch. Returns:
 *   `reason` — non-null ⇒ Save is disabled AND the PUT is refused.
 *   `banner` — the copy THIS panel must render in its own slot; `null` when
 *              `SummaryFields` already shows that same sentence inline
 *              beside the offending field, so the doctor never reads the
 *              identical message twice on one screen.
 * `busy`/`loading`/`loadError` disable the button too but explain themselves
 * via the button label / panel state, so they are not reasons here.
 *
 * Integration (FE-3 → FE-4 glue): `getHeaderDateRangeError(fields, values)`
 * is `SummaryFields.jsx`'s exported client mirror of the server's 422
 * `INVALID_DATE_RANGE` (§2.7 — effective discharge datetime precedes the
 * effective admission datetime). It takes BOTH the `fields[]` array (it
 * resolves each header's effective `value ?? prefill` through the same
 * single-source resolver the inputs use) and the edited `values` map — the
 * two-arg signature is FE-3's real export, not the one-arg placeholder the
 * FE-4 TODO guessed at. SummaryFields renders the WHY inline
 * (`summary-header-date-range-error`), so `banner` stays null for it.
 *
 * `canEdit` (R3, bk-1202) — a viewer who holds `VIEW_ADMISSION` but not
 * `DISCHARGE_PATIENT` (a NURSE) could open this tab, type a whole summary,
 * and only find out it's rejected when the PUT 403s. Checked BEFORE the
 * date-range mirror (a permission gap is the more fundamental reason), the
 * SAME ratified sentence the reactive 403 fallback shows
 * (`NOT_PERMITTED_SUMMARY_COPY`, single-sourced from `admissionDischargeApi`)
 * — so the doctor/nurse never reads two different sentences for the one
 * cause. `locked` still wins over both — a closed admission is closed
 * regardless of who's looking at it.
 *
 * `getInvestigationRowsError` (FE-C2, D4/D5 round-1 rework) — the client-side
 * mirror of the server's row caps for any `kind:"lab_lines"` field (today
 * only `investigations`): over 40 rows, a name over 120 chars, or a result
 * over 200 chars. Checked last (the date-range gap is the more fundamental
 * one when both are wrong at once); shown as THIS panel's own banner since
 * `InvestigationRowsField` doesn't render a length-violation message inline
 * itself (only its own 40-row cap reason).
 *
 * `viewerNotTreating` (admission-access M-FE-3) — a DOCTOR who is neither the
 * primary nor an added doctor of THIS admission: the server refuses the PUT
 * with 403 NOT_TREATING_DOCTOR, so Save is disabled up front with the ratified
 * sentence (never a Save that errors). Same tier as `canEdit`, after it (a
 * missing slug is the more fundamental reason).
 *
 * `doctorRequired` (owner ruling H, 2026-09-13) — checked right after the
 * permission gates (a missing treating doctor is a more fundamental block
 * than a content issue, same tier as `canEdit`). `banner` is `null` because
 * this panel renders its OWN dedicated top banner (with the "Assign doctor"
 * action) when `doctorRequired` is true — the footer would otherwise repeat
 * the identical sentence a second time on one screen.
 */
function deriveSaveBlockedReason(locked, canEdit, viewerNotTreating, doctorRequired, fields, values) {
  if (locked) return { reason: LOCKED_NOTE, banner: LOCKED_NOTE };
  if (!canEdit) return { reason: NOT_PERMITTED_SUMMARY_COPY, banner: NOT_PERMITTED_SUMMARY_COPY };
  if (viewerNotTreating) return { reason: NOT_TREATING_SUMMARY_COPY, banner: NOT_TREATING_SUMMARY_COPY };
  if (doctorRequired) return { reason: DOCTOR_REQUIRED_COPY, banner: null };
  const dateRangeError = getHeaderDateRangeError(fields, values);
  if (dateRangeError) return { reason: dateRangeError, banner: null };
  const rowsError = getInvestigationRowsError(fields, values);
  if (rowsError) return { reason: rowsError, banner: rowsError };
  return { reason: null, banner: null };
}

/**
 * DischargeSummaryPanel (DISCHARGE-TEMPLATES Phase 1 · FE-4) — loads the
 * spec-driven summary on open, renders it through `SummaryFields`, and
 * saves the whole `{values}` map on Save (§2.3 full-object REPLACE — every
 * editable key currently held, header keys included once FE-3 wires their
 * inputs).
 *
 * Load: ONE `getDischargeSummary` call per `admissionId` (stable primitive
 * effect dep, no request storm — `feedback_runtime_safety_no_request_storms`).
 * Save reuses the exact `getDischargeSummary`-shaped response the PUT
 * returns (§2.3 — byte-identical envelope) so a save never needs a second
 * GET to refresh the form.
 *
 * Lock rule — UNCHANGED from the shipped C5/FE-9 behaviour, NOT the
 * server's `locked` flag: `isTerminal(status) && !isDeathOutcome(status)`,
 * same derived source, same ratified copy, so BE-7's DECEASED
 * `save_summary` carve-out stays reachable from this tab exactly as before.
 *
 * Cause-of-death stays SummaryFields' read-only render off `admission` —
 * this panel never touches it (single writer: DischargeDialog's
 * `/discharge/complete`, INV-7, unchanged).
 *
 * DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — mounts FE-1's
 * `useDischargeSnippetStore` for every `field.snippets` key `fields[]`
 * carries: ONE `load()` per (admissionId, field) on mount, re-fired only
 * when the admission or the snippet-enabled key SET genuinely changes (a
 * stable joined-string effect dep, never a fresh array — no request storm);
 * `SnippetPicker`'s `onOpen` calls the SAME idempotent `load()` again,
 * harmlessly. `snippetDataByField` re-keys the store's `byKey` (which folds
 * in the admission id) down to canonical-field-key only — neither
 * `SummaryFields` nor `SnippetPicker` ever sees an admission id (§2.1).
 * `onCreateSnippet`/`onDeleteSnippet` bind the store's actions to THIS
 * admission; a successful create also fires a transient toast
 * (`snippetToast`) — independent of the summary's own `saved`/`error`
 * banners, which are about the SUMMARY, not a template. No extra
 * "invalidate" step is needed on top of what the store already does
 * locally on create/delete (FE-1) — the Zustand selector below re-renders
 * this panel, and therefore `SnippetPicker`'s list, automatically.
 *
 * @param admission     admission row (id, status, cause_of_death, etc. — see SummaryFields JSDoc)
 * @param dischargeType DISCHARGE_TYPE key or null
 * @param canPrint      holds GENERATE_IPD_BILL_PDF
 * @param onAssignDoctor  optional () → opens the existing "Change primary doctor" dialog
 *                          (owner ruling H). Rendered only when the caller supplies it —
 *                          this panel never builds its own picker (memory: prefer_minimal_repurpose).
 * @param patientDetailsRefreshToken  AGE-FE (bk-1292) — a monotonic integer, bumped by the
 *                          caller exactly once per successful save on the SIBLING "Patient
 *                          Details" dialog (age/address/allergies). Same "notice a sibling
 *                          dialog's edit only through a stable-primitive prop, never a
 *                          callback" pattern ruling H's `doctorMemberId` already uses below —
 *                          the summary GET is re-fetched exactly once per bump so a freshly
 *                          edited age shows up on the Summary tab without a manual reload.
 * @param viewerNotTreating  admission-access M-FE-3 — the viewer is a DOCTOR outside this
 *                          admission's care team (resolved by `DischargeSummaryPanel` below,
 *                          never passed in): Save disabled + the ratified reason, template
 *                          controls absent. Print is unchanged.
 */
function DischargeSummaryPanelBody({
  admission, dischargeType = null, canPrint = false, onAssignDoctor, patientDetailsRefreshToken,
  onSummaryPrinted, viewerNotTreating = false,
}) {
  const admissionId = admission && admission.admission_id;
  // Owner ruling H (2026-09-13) — the SAME denormalized field
  // `AdmissionWorkspace`'s "Primary doctor" line and `DischargeDialog`'s own
  // gate read (single source). A stable primitive, safe as an extra effect
  // dep below — it only changes when the admission is genuinely reassigned,
  // never per render (memory: no request storms).
  const doctorMemberId = (admission && admission.primary_doctor_member_id) || null;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // admission-access — the department wall refused this admission: the shared panel, not an error box.
  const [outOfScope, setOutOfScope] = useState(false);
  const [summary, setSummary] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showUnsavedPrintModal, setShowUnsavedPrintModal] = useState(false);
  const [printingSummary, setPrintingSummary] = useState(false);
  // Ruling H — sticky reveal for the RACE: the GET that loaded this summary
  // said `doctor_required` was absent/false, but the save itself 409'd
  // DOCTOR_REQUIRED (the doctor was unassigned in the meantime). Reset only
  // on the next genuine load (admissionId/doctorMemberId change below) —
  // never cleared by a later unrelated save failure.
  const [doctor409, setDoctor409] = useState(false);
  // verdict-r2.md ruling C-3 — { [fieldKey]: { message, row? } }, built from
  // the LAST save's 422 `key`/`row` extras. Cleared on a fresh load and the
  // instant the doctor edits that same field again (stale-error guard, same
  // discipline as `saved`/`dirty` below).
  const [fieldErrors, setFieldErrors] = useState({});
  // FIX-7-FE — local-override sentinel, the SAME idiom `AdmissionWorkspace`'s
  // own `plannedDischargeOverride` uses: `undefined` = "no local override
  // yet, read off the `admission` prop"; `null` is a LEGITIMATE override
  // value (a just-cleared plan), so the sentinel must not collapse the two.
  // This panel owns its own copy (rather than reading a merged prop from
  // `AdmissionWorkspace`) so a plan save here reflects immediately without
  // waiting on a sibling surface's own refresh plumbing.
  const [dischargeDateOverride, setDischargeDateOverride] = useState(undefined);

  // T-R2 (bk-752 narrow slice) — one derived source, never re-derived inline.
  const isDeath = isDeathOutcome(admission && admission.status);
  // FE-9 — locked at every terminal status EXCEPT DECEASED (BE-7 carve-out);
  // the SAME rule the care-team card locks on (one shared predicate).
  const locked = isCareTeamLocked(admission && admission.status);
  // S5 AC1/AC2 — the sheet prints as a DRAFT until the discharge is initiated
  // and as the final after it (the RENDERER owns the watermark/DOD cell; this
  // is the same ONE predicate driving the button's label, so the control can
  // never promise a final sheet the server would print as a draft).
  const dodConfirmed = dodIsConfirmed(admission);
  // R3 (bk-1202) — the existing permission hook, checked once, single source
  // for the Save gate below (never re-derived per branch/render).
  const canEdit = usePermission(DISCHARGE_PERMISSIONS.DISCHARGE_PATIENT);
  // FIX-7-FE — the SAME OR-gate `PlanDischargeDialog`'s own trigger uses
  // (ADMIT_PATIENT OR DISCHARGE_PATIENT), gating ONLY the `discharged_at`
  // field's edit controls pre-completion.
  const canAdmitPatient = usePermission(ADMISSION_PERMISSIONS.ADMIT_PATIENT);
  const canEditDischargeDate = canAdmitPatient || canEdit;
  // Server-given, pre-ordered, pre-filtered (§2.2) — this panel adds nothing.
  const fields = (summary && summary.fields) || [];
  // Ruling H — the GET/PUT envelope's additive `doctor_required` (absent ⇒
  // false, §2.2/§2.3 forward-compat) UNIONED with the sticky 409 above; ONE
  // derived value, drives the top banner, the Save/Print gate AND the reason.
  const doctorRequired = !!(summary && summary.doctor_required) || doctor409;
  // Single source for "why can't I save" — locked note, permission gate,
  // not-a-treating-doctor, doctor-required AND the date-range mirror.
  const { reason: saveBlockedReason, banner: saveBlockedBanner } = deriveSaveBlockedReason(locked, canEdit, viewerNotTreating, doctorRequired, fields, values);
  // FIX-7-FE — the plan `SummaryFields.jsx`'s `DischargeDateField` reads/
  // writes, local override merged over the raw prop (own JSDoc above).
  const plannedDischargeAt = dischargeDateOverride !== undefined
    ? dischargeDateOverride
    : ((admission && admission.planned_discharge_at) || null);
  const summaryAdmission = dischargeDateOverride !== undefined
    ? { ...admission, planned_discharge_at: dischargeDateOverride }
    : admission;
  const dischargeDateCompleted = isTerminal(admission && admission.status);
  const saveDischargeDatePlan = useCallback(async (iso) => {
    if (!admissionId) return { success: false };
    const res = await patchAdmission(admissionId, { planned_discharge_at: iso });
    if (res.success) {
      setDischargeDateOverride(res.data ? (res.data.planned_discharge_at ?? null) : iso);
    }
    return res;
  }, [admissionId]);
  // Owner ruling I addendum (DOD-WARN-FE) — WARNING only, never a Save/Print
  // block. Pre-completion "empty" now means "no discharge plan set" (the
  // SAME `plannedDischargeAt` `DischargeDateField` itself reads, so the two
  // can never disagree); post-completion it stays the ORIGINAL single-source
  // `isDischargeDateEmpty` helper (the recorded/legacy-override mirror).
  // Hidden once the admission is locked (nothing left to fill in), or when
  // the catalogue carries no `discharged_at` key at all (org hid it, §2.7
  // forward-compat) — never fed into `deriveSaveBlockedReason` above.
  const hasDischargedAtField = fields.some((f) => f && f.key === 'discharged_at');
  const dodEmpty = !locked && hasDischargedAtField && (dischargeDateCompleted
    ? isDischargeDateEmpty(fields, values)
    : !plannedDischargeAt);

  // DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — dispatched by the SAME
  // `field.snippets` flag `SummaryFields.jsx` dispatches on, never a key
  // literal. A stable STRING (never a fresh array/object) so it is safe as
  // an effect dep below (memory: no request storms — an array literal
  // rebuilt every render would re-fire the effect every render).
  const snippetFieldKeysStr = fields.filter((f) => f && f.snippets).map((f) => f.key).join(',');
  const [snippetToast, setSnippetToast] = useState(null);

  // Single-flight load, ONE per (admissionId, field) — the store's own
  // `load()` is itself idempotent (no-op once 'ready' or already in-flight,
  // FE-1), so this effect firing again with the SAME two primitives is a
  // guaranteed no-op; it only fires again when the admission or the set of
  // snippet-enabled catalogue keys genuinely changes.
  useEffect(() => {
    if (!admissionId || !snippetFieldKeysStr) return;
    snippetFieldKeysStr.split(',').forEach((field) => {
      useDischargeSnippetStore.getState().load(admissionId, field);
    });
  }, [admissionId, snippetFieldKeysStr]);

  // Also offered as `SnippetPicker`'s `onOpen` (FE-2) — harmless/idempotent
  // alongside the mount-time load above (the store dedupes either way); a
  // future lazy-only field needs no plumbing change here.
  const onOpenSnippets = useCallback((field) => {
    if (!admissionId) return;
    useDischargeSnippetStore.getState().load(admissionId, field);
  }, [admissionId]);

  const onCreateSnippet = useCallback(async (field, payload) => {
    if (!admissionId) return { success: false };
    const res = await useDischargeSnippetStore.getState().createSnippet(admissionId, field, payload);
    if (res.success) {
      // §A #10 — names the treating doctor the template was saved for; the
      // entry is already loaded by this panel's own eager-load effect (or by
      // opening the picker to reach "Save as template" in the first place),
      // so this is a cache read, never a new request.
      const entry = useDischargeSnippetStore.getState().byKey[dischargeSnippetKey(admissionId, field)];
      setSnippetToast(snippetSavedToastCopy(entry?.ownerDoctorName));
      setTimeout(() => setSnippetToast(null), 3000);
    }
    return res;
  }, [admissionId]);

  const onDeleteSnippet = useCallback((field, snippetId) => {
    if (!admissionId) return Promise.resolve({ success: false });
    return useDischargeSnippetStore.getState().deleteSnippet(admissionId, field, snippetId);
  }, [admissionId]);

  // Re-keyed BY CANONICAL FIELD (not the store's own {admissionId}::{field}
  // key) — `SummaryFields`/`SnippetPicker` never see an admission id (§2.1).
  // The Zustand selector re-renders this panel whenever ANY key's entry
  // changes (create/delete/load) — that re-render is what "invalidates" the
  // picker's list; no separate invalidation call is needed on top of what
  // FE-1's store already does locally on create/delete. Computed PLAINLY,
  // not `useMemo`'d — `fields` itself is a fresh array on every render
  // (`(summary && summary.fields) || []`, unchanged from before this task),
  // so memoising on it would never hit and only add a dependency the linter
  // flags; the filter/forEach below is cheap and carries no fetch of its
  // own, so recomputing it every render is not a request-storm risk.
  const snippetByKey = useDischargeSnippetStore((s) => s.byKey);
  const snippetDataByField = {};
  if (admissionId) {
    fields.forEach((f) => {
      if (!f || !f.snippets) return;
      snippetDataByField[f.key] = snippetByKey[dischargeSnippetKey(admissionId, f.key)];
    });
  }

  // Single-flight load — fires once per admissionId, PLUS once more if the
  // admission's primary doctor changes (ruling H: the "Assign doctor" action
  // lives on a SIBLING dialog, so this panel notices the reassignment only
  // through this prop, not a callback), PLUS once more per
  // `patientDetailsRefreshToken` bump (AGE-FE/bk-1292 — same reasoning: the
  // "Patient Details" age edit is a SIBLING dialog too) — all three are
  // stable primitives, so this can never turn into a request storm.
  useEffect(() => {
    if (!admissionId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setOutOfScope(false);
    // bk-1198 — a saved/error banner from the PREVIOUS admission must not
    // survive a switch to a new one; reset the moment a new admissionId is
    // loaded, not only on this load's own success/failure.
    setError(null);
    setSaved(false);
    setDirty(false);
    setDoctor409(false);
    setFieldErrors({});
    setDischargeDateOverride(undefined);
    getDischargeSummary(admissionId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success) {
        setSummary(res.data);
        setValues(buildEditableValues(res.data && res.data.fields));
        setDirty(false);
      } else if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) {
        setOutOfScope(true);
      } else {
        setLoadError(res.error || 'Could not load the discharge summary. Please try again.');
      }
    });
    return () => { cancelled = true; };
  }, [admissionId, doctorMemberId, patientDetailsRefreshToken]);

  const onChange = useCallback((key, nextValue) => {
    setValues((v) => ({ ...v, [key]: nextValue }));
    setSaved(false);
    setDirty(true);
    // C-3 — editing a field clears its own stale 422, never the others'.
    setFieldErrors((errs) => {
      if (!errs[key]) return errs;
      const next = { ...errs };
      delete next[key];
      return next;
    });
  }, []);

  const saveSummary = async () => {
    if (busy || saveBlockedReason || !admissionId) return { success: false };
    setBusy(true);
    setError(null);
    const res = await saveDischargeSummary(admissionId, values);
    setBusy(false);
    if (res.success) {
      setSummary(res.data);
      setValues(buildEditableValues(res.data && res.data.fields));
      setSaved(true);
      setDirty(false);
      setFieldErrors({});
      setDoctor409(false);
      // R20 reconcile (dt-p1b full fold) — item-mode fields are learned
      // SERVER-SIDE from the value we just saved, so nothing on this client
      // knows the new rows exist. Mark them stale; the next picker open
      // refetches. Read off the RESPONSE's catalogue (never a key literal),
      // so a future item-mode key needs no change here.
      ((res.data && res.data.fields) || [])
        .filter((f) => f && f.snippet_mode === 'item')
        .forEach((f) => useDischargeSnippetStore.getState().invalidate(admissionId, f.key));
    } else if (res.code === DISCHARGE_ERROR_CODE.ADMISSION_LOCKED) {
      setFieldErrors({});
      setError(LOCKED_NOTE);
    } else if (res.code === DISCHARGE_ERROR_CODE.DOCTOR_REQUIRED) {
      // Ruling H race — the dedicated top banner (driven by `doctorRequired`)
      // already states the reason with the "Assign doctor" action; no second,
      // duplicate banner here.
      setFieldErrors({});
      setDoctor409(true);
    } else if (res.key && fieldErrorCopy(res.code, '_', res)) {
      // verdict-r2.md ruling C-3 — a field-named 422/413 renders under THAT
      // field only (`fields[key]`'s own `label`); no generic banner for it.
      // Gated on `fieldErrorCopy` recognising the CODE (not merely on `key`
      // being present) — several existing codes (FIELD_HIDDEN, READ_ONLY_
      // FIELD, INVALID_DATETIME, …) also carry a `key` extra but keep their
      // existing ratified BANNER copy, unaffected by this ruling.
      const field = fields.find((f) => f && f.key === res.key);
      const label = (field && field.label) || res.key;
      const message = fieldErrorCopy(res.code, label, res);
      setFieldErrors({ [res.key]: { message, row: res.row } });
    } else {
      setFieldErrors({});
      setError(res.error || 'Could not save the discharge summary. Please try again.');
    }
    return res;
  };

  const handleSave = () => { saveSummary(); };

  // FIX-7-FE — opens in the app's own viewer (Print/Close chrome), never a
  // new tab. Single-flight via `printingSummary` (mirrors `busy`/Save's own
  // disable-on-click guard, same file convention): the button disables the
  // instant this fires, so a doctor mashing the button can't fan out N
  // fetches. Failures render through the SAME `error` banner Save already
  // owns — one slot, never a native alert.
  const printSummary = useCallback(async () => {
    if (printingSummary || !admissionId) return { success: false };
    setPrintingSummary(true);
    setError(null);
    const res = await openDischargeSummaryPdf(admissionId, { title: isDeath ? 'Death summary' : 'Discharge summary' });
    setPrintingSummary(false);
    if (!res.success) setError(res.error || 'Could not open the discharge summary.');
    // S2 AC9 — only a FINAL (post-initiate) print earns the reprint nudge on a
    // later date change; a draft print never does.
    else if (dodConfirmed && typeof onSummaryPrinted === 'function') onSummaryPrinted();
    return res;
  }, [admissionId, isDeath, printingSummary, dodConfirmed, onSummaryPrinted]);

  // Print reads the SAVED value only (§2.2) — an unsaved edit would print a
  // stale sheet, so a dirty form offers a choice via the in-app modal, never
  // a native `confirm()`/`beforeunload`.
  const handlePrintClick = () => {
    if (dirty) {
      setShowUnsavedPrintModal(true);
    } else {
      printSummary();
    }
  };

  const handleSaveAndPrint = async () => {
    setShowUnsavedPrintModal(false);
    const res = await saveSummary();
    if (res.success) printSummary();
  };

  const handlePrintWithoutSaving = () => {
    setShowUnsavedPrintModal(false);
    printSummary();
  };

  const validForType = isSummaryValid(dischargeType, admission);

  if (outOfScope) return <NoAccessPanel variant={NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT} />;

  return (
    <div data-testid="discharge-summary-panel">
      {loading && (
        <div className="text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="summary-loading">
          Loading discharge summary…
        </div>
      )}

      {!loading && loadError && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="summary-load-error">
          {loadError}
        </div>
      )}

      {!loading && !loadError && (
        <>
          {/* AGE-FE (bk-1292), display fixed under FIX-FE (ruling 1,
              copy-ratified-slice1.md) — "Age / Sex" was invisible on this
              tab (grep clean before AGE-FE). Sourced from the GET
              envelope's two DISPLAY-READY additive fields,
              `patient_age_display` / `patient_gender_display` — the raw
              `patient_gender` enum stays on the wire but is NEVER read
              here; this panel never formats age or gender itself, and
              never falls back to the raw enum (single source: the server
              owns both derivations). Either value absent/blank -> that
              half is simply omitted from the joined line; BOTH absent ->
              the whole row is omitted, never a fake dash or "0" (same
              bk-1201 posture the PRINT block already has). */}
          {summary && (summary.patient_age_display || summary.patient_gender_display) && (
            <div className="mb-3 text-[12.5px] text-slate-600 dark:text-slate-300" data-testid="summary-age-sex">
              <span className="font-semibold text-slate-500 dark:text-slate-400">Age / Sex: </span>
              {[summary.patient_age_display, summary.patient_gender_display].filter(Boolean).join(' / ')}
            </div>
          )}

          {summary && summary.has_hidden_values && (
            <div className="mb-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="summary-hidden-notice">
              {HIDDEN_VALUES_NOTE}
            </div>
          )}

          {/* Owner ruling H — no discharge summary without a treating doctor.
              Dedicated top banner (with the "Assign doctor" action) rather
              than the generic footer slot, so the reason is visible before
              the doctor scrolls down to Save. */}
          {doctorRequired && (
            <div
              className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5 flex items-center justify-between gap-2 flex-wrap"
              data-testid="summary-doctor-required-banner"
            >
              <span>{DOCTOR_REQUIRED_COPY}</span>
              {onAssignDoctor && (
                <button type="button" className={btnSecondary} onClick={onAssignDoctor} data-testid="summary-assign-doctor">
                  Assign doctor
                </button>
              )}
            </div>
          )}

          <SummaryFields
            admission={summaryAdmission}
            dischargeType={dischargeType}
            fields={fields}
            value={values}
            onChange={onChange}
            errors={fieldErrors}
            locked={locked}
            canEditSummary={canEdit && !viewerNotTreating}
            snippetDataByField={snippetDataByField}
            onOpenSnippets={onOpenSnippets}
            onCreateSnippet={onCreateSnippet}
            onDeleteSnippet={onDeleteSnippet}
            canEditDischargeDate={canEditDischargeDate}
            onSaveDischargeDate={saveDischargeDatePlan}
          />

          {/* UX v2 spec §6 — banners + Save/Print sit in one sticky footer so
              the last narrative field is never hidden behind it. JSX order
              and every gating condition below is UNCHANGED, only wrapped.
              `-mx-4`/`px-4` bleeds to the edges of AdmissionWorkspace.jsx's
              own `p-4` wrapper then re-establishes the same inset. FIX-5-FE
              — the ancestor `workspace-scroll` no longer scrolls internally
              (AdmissionDeskPage's fixed-height clip was the bug this task
              fixed), so `sticky bottom-0` now resolves against the real
              viewport: it pins to the bottom of the browser window while
              the page scrolls through the form, same visual intent as
              before. */}
          <div className="sticky bottom-0 z-10 -mx-4 px-4 pt-3 pb-3 mt-5 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700" data-testid="summary-action-footer">
            {dodEmpty && (
              <div
                className="mb-2 rounded-lg border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-[12.5px] p-2.5"
                role="status"
                data-testid="summary-dod-empty-footer-warning"
              >
                {DOD_EMPTY_WARNING_COPY}
              </div>
            )}
            {saveBlockedBanner && (
              <div className="mb-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 p-2.5" data-testid="summary-locked-note">
                {saveBlockedBanner}
              </div>
            )}
            {error && (
              <div className="mb-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="summary-error">
                {error}
              </div>
            )}
            {saved && !error && (
              <div className="mb-2 rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[12.5px] p-2.5" data-testid="summary-saved">
                {isDeath ? 'Death summary saved.' : 'Discharge summary saved.'}
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center justify-between">
              <button type="button" className={btnPrimary} onClick={handleSave} disabled={busy || !!saveBlockedReason} data-testid="summary-save">
                {busy ? 'Saving…' : (isDeath ? 'Save death summary' : 'Save discharge summary')}
              </button>
              {canPrint && admissionId && (
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={handlePrintClick}
                  disabled={printingSummary || doctorRequired}
                  title={doctorRequired ? DOCTOR_REQUIRED_COPY : undefined}
                  data-testid="summary-print"
                >
                  {/* S5 — "Print draft summary" until the discharge is
                      initiated, "Print discharge summary" after it (and after
                      Complete); a death sheet keeps its own label. */}
                  {printingSummary ? 'Opening…' : (isDeath ? 'Print death summary' : (dodConfirmed ? 'Print discharge summary' : 'Print draft summary'))}
                </button>
              )}
            </div>
          </div>
          {/* validForType exposed for a hosting stepper's own "Next"/"Complete" gate — this panel itself never blocks the PUT on it. */}
          <span data-testid="summary-valid-for-type" data-valid={String(validForType)} className="hidden" />
        </>
      )}

      {showUnsavedPrintModal && (
        <ConfirmationModal
          isOpen
          title="Unsaved changes"
          message="Save the discharge summary before printing, so the printed copy includes your changes."
          ctaLabel="Save & print"
          onConfirm={handleSaveAndPrint}
          cancelLabel="Print without saving"
          onCancel={handlePrintWithoutSaving}
        />
      )}

      {/* DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — transient confirmation for a
          successful "Save as template" (independent of the summary's own
          `saved`/`error` banners above, which are about the SUMMARY, not a
          template). Auto-dismisses; never blocks anything underneath it. */}
      {snippetToast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1200] rounded-lg bg-teal-700 dark:bg-teal-600 text-white text-[12.5px] font-semibold px-4 py-2 shadow-lg"
          role="status"
          data-testid="summary-snippet-toast"
        >
          {snippetToast}
        </div>
      )}
    </div>
  );
}

/**
 * The summary panel (props: see `DischargeSummaryPanelBody`). Resolves
 * `viewerNotTreating` — a DOCTOR outside the admission's care team — from the
 * row's `viewer_relationship` + the viewer's role (`NonTreatingDoctorGate`:
 * contract C-7, `canEdit = DISCHARGE_PATIENT && !(DOCTOR && no relationship)`).
 */
export default function DischargeSummaryPanel(props) {
  return (
    <NonTreatingDoctorGate admission={props.admission}>
      {(viewerNotTreating) => <DischargeSummaryPanelBody {...props} viewerNotTreating={viewerNotTreating} />}
    </NonTreatingDoctorGate>
  );
}
